'use client';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { AhafoodLogo } from '@ahamove/icons/order';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, JSFunction } from '@ahm/common-helpers';
import { getCookie } from 'cookies-next';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, Search, X } from 'lucide-react';
import { useParams, useSearchParams } from 'next/navigation';
import { useRef, useEffect } from 'react';
import SearchAndFilterBar from './home-search';
import AmoriBanner from '@/app/[locale]/(main)/store/components/amori-banner';
import NotificationDemo from '@/app/[locale]/(main)/store/components/store-notification';
import UserButton from '@/components/user/user-button';
import { publicEnv } from '@/config/public-env.config.mjs';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { usePathname, useRouter, Link } from '@/i18n/routing';
import MainHeader from '@/layouts/main/main-header';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { useAuth } from '@/providers/auth-provider';
import { CITIES, useAiFoodOrderStore } from '@/store';

export default function StoreHeader() {
  const params = useParams();
  const searchParams = useSearchParams();
  const text = searchParams.get('text');
  const latParam = searchParams.get('lat');
  const lngParam = searchParams.get('lng');
  const address = useAiFoodOrderStore.use.address();
  const citySlug = Array.isArray(params.city) ? params.city[0] : params.city;
  const cityLabel = CITIES.find((c) => c.slug === citySlug)?.label ?? citySlug;
  const router = useRouter();
  const pathName = usePathname();
  const isMobile = useIsMobile();
  const headerRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 130], [1, 0]);
  const barOpacity = useTransform(scrollY, [0, 130], [0, 1]);
  const pointerEvents = useTransform(opacity, (v) =>
    v < 0.1 ? 'none' : 'auto'
  );
  const barPointerEvents = useTransform(barOpacity, (v) =>
    v > 0.1 ? 'auto' : 'none'
  );

  const isWebInApp = useDetectWebInApp();
  const addressCookie = getCookie('ahafood_address');
  const { isLoading } = useAuth();

  // Use context for centralized sheet management
  const { openCitySheet, isNavigating } = useStoreCitySheet();

  useEffect(() => {
    // Only open city sheet if no address in cookie/store AND no lat/lng in URL AND not loading
    // URL params have highest priority and are handled in auth-provider
    if (
      !addressCookie &&
      !address &&
      !isNavigating &&
      !isLoading && // Don't open sheet while auth-provider is loading
      !latParam &&
      !lngParam
    ) {
      console.log('🏠 Opening city sheet - no address and no URL params');
      openCitySheet();
    } else if (isLoading && (latParam ?? lngParam)) {
      console.log('🏠 Skipping city sheet - URL params being processed');
    }
  }, [
    addressCookie,
    address,
    isNavigating,
    isLoading,
    openCitySheet,
    latParam,
    lngParam,
  ]);

  // Note: Removed city mismatch logic as it was causing duplicate sheet opening
  // The city change is now handled entirely within StoreCityIdSheet component

  if (pathName.includes('/danh-sach-cua-hang')) {
    // On store listing pages, the sheet is managed centrally by context
    return null;
  }

  // In-app header for WebInApp
  if (isWebInApp) {
    return (
      <>
        <NotificationDemo />

        <div className="flex h-12 w-full z-[99] flex-row items-center justify-center gap-2 px-4 py-2 border-b sticky top-0 inset-x-0 bg-white">
          <Button
            variant="ghost"
            className="absolute left-1.5 top-1/2 -translate-y-1/2 size-10 flex-none p-2 [&_svg]:size-6"
            onClick={() => JSFunction.call({ name: 'close' })}
          >
            <X />
          </Button>
          <Link className="flex flex-none items-center justify-start" href={''}>
            <AhafoodLogo className="h-10 w-36" />
            <h1 className="sr-only">{publicEnv.NEXT_PUBLIC_APP_TITLE}</h1>
          </Link>
        </div>
        <header
          className={cn(
            'relative z-50 overflow-hidden px-4 pb-8 flex flex-col max-w-screen-xl mx-auto'
          )}
          style={{
            backgroundImage: `url(${assetPrefix}/static/images/mb-header-bg.webp)`,
          }}
        >
          <div className="relative flex flex-row items-center justify-between h-16 py-4  w-full">
            <div className="flex items-center">
              <Card
                className="flex flex-col gap-0 hover:bg-none bg-transparent text-left px-0 border-none cursor-pointer shadow-none"
                onClick={openCitySheet}
              >
                <span className="text-white text-lg font-bold flex items-center">
                  Địa chỉ nhận món{' '}
                  <ChevronDown className="ml-2 size-4 text-white" />
                </span>
                <div className="flex items-center">
                  <span className="text-white text-xs font-bold mr-1 line-clamp-1 max-w-[calc(100vw-50px)]">
                    {address?.address ?? cityLabel}
                  </span>
                </div>
              </Card>
            </div>
          </div>

          <div className="mb-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="size-5" />
              </div>
              <input
                onClick={() => {
                  // navigate to store list with focus flag
                  router.push(`/${citySlug}/danh-sach-cua-hang?focus=1`);
                }}
                value={text ?? ''}
                readOnly
                type="text"
                placeholder="Bạn đang thèm gì nào?"
                className="w-full pl-12 pr-4 py-3 bg-white rounded-xl text-neutral-90 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
          </div>

          <AmoriBanner className="p-0 bg-transparent" />
        </header>
      </>
    );
  }

  return (
    <>
      {isMobile ? (
        <>
          <NotificationDemo />

          <motion.div
            ref={headerRef}
            style={{
              opacity,
              pointerEvents,
              backgroundImage: `url(${assetPrefix}/static/images/mb-header-bg.webp)`,
            }}
            className="relative overflow-hidden px-4 pb-8 flex flex-col "
          >
            <header
              className={cn(
                'sticky top-0 z-50 mx-auto flex flex-col h-auto w-full max-w-screen-xl md:gap-4'
              )}
            >
              <div className="relative flex flex-row items-center justify-between h-16 py-4 w-full">
                <div className="flex items-center flex-1 min-w-0">
                  <Card
                    className="flex flex-col gap-0 hover:bg-none bg-transparent text-left px-0 border-none cursor-pointer shadow-none"
                    onClick={() => openCitySheet()}
                  >
                    <span className="text-white text-lg font-bold flex items-center">
                      Địa chỉ nhận món
                      <ChevronDown className="ml-2 size-4 text-white" />
                    </span>
                    <div className="flex items-center">
                      <span className="text-white text-xs font-bold mr-1 truncate max-w-[calc(100vw-150px)]">
                        {address?.address ?? cityLabel}
                      </span>
                    </div>
                  </Card>
                </div>

                <UserButton />
              </div>

              <div className="mb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="size-5" />
                  </div>
                  <input
                    onClick={() => {
                      // navigate to store list with focus flag
                      router.push(`/${citySlug}/danh-sach-cua-hang?focus=1`);
                    }}
                    value={text ?? ''}
                    readOnly
                    type="text"
                    placeholder="Bạn đang thèm gì nào?"
                    className="w-full pl-12 pr-4 py-3 bg-white rounded-xl text-neutral-90 focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                </div>
              </div>

              <AmoriBanner className="p-0 bg-transparent" />
            </header>
          </motion.div>
          <motion.div
            style={{ opacity: barOpacity, pointerEvents: barPointerEvents }}
            className="fixed top-0 inset-x-0 z-20 mx-auto max-w-lg bg-white shadow-md"
          >
            <header className={cn('sticky top-0 z-50 max-w-screen-xl mx-auto')}>
              {/* <SearchAndFilterBar /> */}
              {/* <FilterBar categories={[]} currentPayload={{}} citySlug={''} /> */}
              <SearchAndFilterBar citySlug={citySlug ?? ''} />
            </header>
          </motion.div>
        </>
      ) : (
        <MainHeader />
      )}
    </>
  );
}
